import { File as FileIcon, Loader2, Paperclip, X } from 'lucide-react'
import { useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { captureException } from '@/domains/observability'

import { requestAttachmentUpload, uploadAttachmentBytes } from './api'

/** paperclip + chips. holds an attachment list in local state; the parent
 * gets a callback with the current ids whenever the list changes so the
 * send mutation can include them. uploads happen synchronously on file
 * pick . by the time the user clicks send, the bytes are already in s3
 * and the only thing flowing through is the attachmentId.
 *
 * the picker is uncontrolled by design: its lifetime is the composer
 * surface. parent calls reset() (via key change) to clear after a send.
 */

export type PendingAttachment = {
  attachmentId: string
  fileName: string
  contentType: string
  byteSize: number
  status: 'uploading' | 'ready' | 'error'
  error?: string
}

const MAX_BYTES = 25 * 1024 * 1024

export function AttachmentPicker({
  attachments,
  onChange,
  disabled = false
}: {
  attachments: PendingAttachment[]
  onChange: (next: PendingAttachment[]) => void
  disabled?: boolean
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [pickerError, setPickerError] = useState<string | null>(null)

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    setPickerError(null)
    const accepted: PendingAttachment[] = []
    for (const file of Array.from(files)) {
      if (file.size > MAX_BYTES) {
        setPickerError(`${file.name} is over the 25 MB limit.`)
        continue
      }
      const placeholder: PendingAttachment = {
        attachmentId: `pending-${crypto.randomUUID()}`,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        byteSize: file.size,
        status: 'uploading'
      }
      accepted.push(placeholder)
    }
    if (accepted.length === 0) return
    const next = [...attachments, ...accepted]
    onChange(next)

    const fileList = Array.from(files).filter(file => file.size <= MAX_BYTES)
    await Promise.all(
      accepted.map(async (placeholder, idx) => {
        const file = fileList[idx]
        if (!file) return
        try {
          const intent = await requestAttachmentUpload({
            fileName: file.name,
            contentType: file.type || 'application/octet-stream',
            byteSize: file.size
          })
          await uploadAttachmentBytes(intent.attachmentId, file)
          onChange(
            replaceAttachment(placeholder.attachmentId, {
              ...placeholder,
              attachmentId: intent.attachmentId,
              status: 'ready'
            })
          )
        } catch (error) {
          captureException(
            error instanceof Error ? error : new Error(String(error))
          )
          onChange(
            replaceAttachment(placeholder.attachmentId, {
              ...placeholder,
              status: 'error',
              error: 'Upload failed'
            })
          )
        }
      })
    )
  }

  /** replace-by-id helper. uses the *latest* attachments array so two
   * concurrent uploads finishing in any order don't clobber each other.
   */
  function replaceAttachment(
    pendingId: string,
    next: PendingAttachment
  ): PendingAttachment[] {
    const live = attachmentsRef.current
    return live.map(item => (item.attachmentId === pendingId ? next : item))
  }
  const attachmentsRef = useRef(attachments)
  attachmentsRef.current = attachments

  function removeAttachment(id: string) {
    onChange(attachments.filter(item => item.attachmentId !== id))
  }

  return (
    <div className='flex flex-col gap-1.5'>
      <div className='flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2'>
        <input
          ref={fileInputRef}
          type='file'
          multiple
          className='hidden'
          onChange={event => {
            const files = event.target.files
            void handleFiles(files)
            event.target.value = ''
          }}
        />
        <Button
          type='button'
          size='sm'
          variant='ghost'
          disabled={disabled}
          onClick={() => fileInputRef.current?.click()}
          className='gap-1.5'
        >
          <Paperclip className='size-3.5' />
          Attach files
        </Button>
        <span className='text-[11px] text-ink-muted'>Up to 25 MB each</span>
      </div>
      {pickerError ? (
        <p className='text-[11px] text-rose-700'>{pickerError}</p>
      ) : null}
      {attachments.length > 0 ? (
        <ul className='flex flex-wrap gap-1.5'>
          {attachments.map(item => (
            <li
              key={item.attachmentId}
              className='flex items-center gap-1.5 rounded-md border border-border bg-page px-2 py-1 text-[12px] text-ink'
            >
              {item.status === 'uploading' ? (
                <Loader2 className='size-3 animate-spin text-ink-muted' />
              ) : (
                <FileIcon className='size-3 text-ink-muted' />
              )}
              <span className='max-w-40 truncate'>{item.fileName}</span>
              <span className='text-[10px] text-ink-muted'>
                {formatBytes(item.byteSize)}
              </span>
              {item.status === 'error' ? (
                <span className='text-[10px] text-rose-700'>
                  {item.error ?? 'failed'}
                </span>
              ) : null}
              <button
                type='button'
                onClick={() => removeAttachment(item.attachmentId)}
                className='-my-1 ml-1 grid size-8 cursor-pointer place-items-center rounded text-ink-muted hover:text-ink sm:my-0 sm:size-auto'
                aria-label='Remove attachment'
              >
                <X className='size-3' />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
